import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import { Send, Clock, CheckCircle2, XCircle, Rocket } from "lucide-react";
import PayPalCheckoutButton from "../components/PayPalCheckoutButton";

const CATS = ["travel", "clothing", "auto", "stocks", "crypto", "electronics", "home", "food", "beauty", "gaming"];

const EMPTY = {
  title: "", description: "", category: "travel", merchant: "", url: "",
  image_url: "", discount: "", original_price: "", sale_price: "",
};

const STATUS_META = {
  pending:  { label: "Pending review", Icon: Clock,        cls: "text-amber-600" },
  approved: { label: "Live",           Icon: CheckCircle2, cls: "text-emerald-600" },
  rejected: { label: "Not accepted",   Icon: XCircle,      cls: "text-black/40" },
};

export default function Submit() {
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);
  const [rank, setRank] = useState(null);

  const loadMine = async () => {
    try {
      const [{ data: subs }, { data: meRank }] = await Promise.all([
        api.get("/deals/my-submissions"),
        api.get("/leaderboard/me").catch(() => ({ data: null })),
      ]);
      setMine(subs);
      setRank(meRank);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { if (user) loadMine(); }, [user]);

  if (authLoading) return <div className="max-w-3xl mx-auto px-6 py-12 font-mono text-xs uppercase tracking-widest text-black/40">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/deals/submit", form);
      toast.success("Thanks — your deal is queued for editor review.");
      setForm(EMPTY);
      loadMine();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="submit-page">
      <div className="mb-10">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-3">Community</div>
        <h1 className="font-serif font-black text-5xl md:text-6xl tracking-tighter">
          Spotted a great deal?<br /><span className="italic font-normal">Share it.</span>
        </h1>
        <p className="text-black/60 mt-4 max-w-xl">
          Submit your find and our editors will review it within 24 hours. Approved deals go live on the homepage — credited to your name.
        </p>
        {rank && rank.approved_count > 0 && (
          <div className="mt-6 inline-flex items-center gap-3 border-2 border-black bg-white px-4 py-2" data-testid="submit-rank-hint">
            <span className="font-serif font-black text-2xl leading-none">{rank.rank ? `#${rank.rank}` : "—"}</span>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#E63946]">Your rank</div>
              <div className="text-xs text-black/60">{rank.approved_count} approved · <Link to="/leaderboard" className="underline hover:text-[#E63946]">see the board</Link></div>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={submit} className="border-2 border-black p-6 md:p-8 bg-white space-y-4 mb-16" data-testid="submit-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="border border-black px-3 py-2" data-testid="submit-title" />
          <input required placeholder="Merchant / brand" value={form.merchant} onChange={(e) => setForm({ ...form, merchant: e.target.value })} className="border border-black px-3 py-2" data-testid="submit-merchant" />
          <input required type="url" placeholder="Deal URL (https://…)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="border border-black px-3 py-2 md:col-span-2" data-testid="submit-url" />
          <input placeholder="Image URL (optional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="border border-black px-3 py-2 md:col-span-2" data-testid="submit-image" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-black px-3 py-2" data-testid="submit-category">
            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input placeholder="Discount (e.g. 40% OFF)" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} className="border border-black px-3 py-2" data-testid="submit-discount" />
          <input placeholder="Original price (optional)" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} className="border border-black px-3 py-2" />
          <input placeholder="Sale price (optional)" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} className="border border-black px-3 py-2" />
          <textarea required placeholder="Why is this a great deal?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-black px-3 py-2 md:col-span-2 min-h-[100px]" data-testid="submit-description" />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946] disabled:opacity-50"
          data-testid="submit-button"
        >
          <Send className="w-4 h-4" strokeWidth={1.5} />
          {submitting ? "Submitting…" : "Submit for review"}
        </button>
      </form>

      <div>
        <h2 className="font-serif font-bold text-3xl tracking-tight mb-1">Your submissions</h2>
        <p className="text-sm text-black/50 mb-6">Track the status of everything you&apos;ve shared.</p>
        {mine.length === 0 ? (
          <div className="border border-dashed border-black/20 p-8 text-center text-sm text-black/50" data-testid="no-submissions">
            You haven&apos;t submitted a deal yet.
          </div>
        ) : (
          <div className="border border-black/10 bg-white divide-y divide-black/10">
            {mine.map((d) => {
              const meta = STATUS_META[d.status] || STATUS_META.pending;
              const Icon = meta.Icon;
              return (
                <div key={d.id} className="flex items-center gap-4 p-4" data-testid={`my-sub-${d.id}`}>
                  <div className="w-14 h-14 bg-black/5 overflow-hidden shrink-0">
                    {d.image_url && <img src={d.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif font-bold truncate">{d.title}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-black/40 mt-1">
                      {d.category} · {d.merchant}
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${meta.cls}`} data-testid={`my-sub-status-${d.id}`}>
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    {meta.label}
                  </div>
                  {d.status === "pending" && !d.boosted && (
                    <BoostButton dealId={d.id} userId={user.id} onBoosted={loadMine} />
                  )}
                  {d.boosted && (
                    <span className="inline-flex items-center gap-1 bg-[#E63946] text-white px-2 py-1 text-[10px] font-bold uppercase tracking-widest" data-testid={`boosted-${d.id}`}>
                      <Rocket className="w-3 h-3" strokeWidth={2} /> Boosted
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-6">
          <Link to="/" className="text-xs font-bold uppercase tracking-widest underline hover:text-[#E63946]">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}

function BoostButton({ dealId, userId, onBoosted }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const buyWithCard = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "boost_submission",
        boost_deal_id: dealId,
        origin_url: window.location.origin,
        user_id: userId,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 border border-[#E63946] text-[#E63946] px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-[#E63946] hover:text-white"
        data-testid={`boost-btn-${dealId}`}
      >
        <Rocket className="w-3 h-3" strokeWidth={2} /> Boost $2.99
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 border-2 border-black bg-white z-20 p-3 shadow-lg" data-testid={`boost-panel-${dealId}`}>
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#E63946] mb-2">Boost — $2.99</div>
          <p className="text-xs text-black/60 mb-3">Jump to the top of the review queue and auto-feature on approval.</p>
          <button
            onClick={buyWithCard}
            disabled={loading}
            className="w-full bg-black text-white py-2 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946] disabled:opacity-50"
            data-testid={`boost-card-${dealId}`}
          >
            {loading ? "…" : "Pay with Card"}
          </button>
          <PayPalCheckoutButton
            payload={{ lookup_key: "boost_submission", boost_deal_id: dealId, origin_url: window.location.origin }}
            onPaid={() => { setOpen(false); toast.success("Boost activated ✓"); if (typeof onBoosted === "function") onBoosted(); }}
            testIdPrefix={`paypal-boost-${dealId}`}
          />
        </div>
      )}
    </div>
  );
}
